import type { PDFDocumentProxy } from "pdfjs-dist";
import * as pdfjs from "pdfjs-dist";
import { Texture } from "pixi.js";

// Configure PDF.js worker
// Use the built-in worker from pdfjs-dist
if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
  ).href;
}

export async function loadPDF(blob: Blob): Promise<PDFDocumentProxy> {
  try {
    // pdfjs.getDocument can handle both regular URLs and base64 data URLs
    // Base64 strings should be in the format: "data:application/pdf;base64,..."
    const loadingTask = pdfjs.getDocument(await blob.arrayBuffer());
    const pdfDocument = await loadingTask.promise;

    return pdfDocument;
  } catch (error) {
    console.error("Error loading PDF:", error);
    throw error;
  }
}

export async function renderPage(
  pdfDocument: PDFDocumentProxy,
  pageNumber: number,
  scale: number
): Promise<Texture> {
  if (pageNumber < 1 || pageNumber > pdfDocument.numPages) {
    throw new Error(
      `Invalid page number: ${pageNumber}. PDF has ${pdfDocument.numPages} pages.`
    );
  }

  try {
    // Get the page
    const page = await pdfDocument.getPage(pageNumber);

    // Calculate viewport
    const viewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d")!;
    const outputScale = window.devicePixelRatio || 1;

    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = Math.floor(viewport.width) + "px";
    canvas.style.height = Math.floor(viewport.height) + "px";
    const transform =
      outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

    // Render page to canvas
    const renderContext = {
      canvasContext: context,
      viewport,
      transform,
      canvas,
    };

    await page.render(renderContext).promise;
    const texture = Texture.from(canvas);
    return texture;
  } catch (error) {
    console.error(`Error rendering page ${pageNumber}:`, error);
    throw error;
  }
}
